import React, { useState } from "react";
import Navbar from "../components/sub-comp/Navbar.jsx";
import Footer from "../components/sub-comp/MainFooter.jsx";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import { 
  FaSearch, 
  FaBook, 
  FaLaptop, 
  FaUsers, 
  FaClock, 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt,
  FaArrowRight,
  FaCalendarAlt,
  FaMicroscope,
  FaGraduationCap,
  FaDatabase,
  FaCoffee,
  FaChair,
  FaArchive,
  FaNewspaper,
  FaVideo,
  FaBookOpen,
  FaGlobe,
  FaDesktop,
  FaHandsHelping,
  FaFilter,
  FaTag
} from "react-icons/fa";

const Library = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  const libraryServices = [
    {
      id: 1,
      title: "Digital Resources & Databases",
      description: "Access to thousands of e-books, academic journals, and research databases.",
      icon: FaDatabase,
      features: ["E-books Collection", "Academic Journals", "Research Databases", "Online Archives"],
      availability: "24/7 Online Access"
    },
    {
      id: 2,
      title: "Study Spaces & Facilities",
      description: "Comfortable and well-equipped study areas for individual and group work.",
      icon: FaChair,
      features: ["Individual Study Carrels", "Group Study Rooms", "Quiet Zones", "Collaborative Spaces"],
      availability: "6:00 AM - 10:00 PM"
    },
    {
      id: 3,
      title: "Research Assistance",
      description: "Expert librarians to help with research projects and information literacy.",
      icon: FaHandsHelping,
      features: ["Research Consultations", "Citation Help", "Database Training", "Literature Reviews"],
      availability: "Monday - Friday, 8:00 AM - 5:00 PM"
    },
    {
      id: 4,
      title: "Technology & Equipment",
      description: "Modern technology and equipment for enhanced learning and research.",
      icon: FaLaptop,
      features: ["Computer Workstations", "Printing & Scanning", "WiFi Access", "Charging Stations"],
      availability: "During Library Hours"
    }
  ];

  const digitalResources = [
    {
      id: 1,
      name: "Academic Search Complete",
      description: "Comprehensive database of academic journals and research papers.",
      icon: FaNewspaper,
      category: "Database",
      access: "On-campus & Remote"
    },
    {
      id: 2,
      name: "IEEE Xplore Digital Library",
      description: "Technical literature in engineering and technology fields.",
      icon: FaMicroscope,
      category: "Database",
      access: "On-campus & Remote"
    },
    {
      id: 3,
      name: "E-book Collection",
      description: "Over 50,000 e-books covering all academic disciplines.",
      icon: FaBook,
      category: "E-books",
      access: "24/7 Online"
    },
    {
      id: 4,
      name: "Video Learning Library",
      description: "Educational videos and multimedia content for various subjects.",
      icon: FaVideo,
      category: "Multimedia",
      access: "On-campus & Remote"
    },
    {
      id: 5,
      name: "Thesis & Dissertations",
      description: "Digital repository of student theses and dissertations.",
      icon: FaGraduationCap,
      category: "Archive",
      access: "On-campus Only"
    },
    {
      id: 6,
      name: "Newspaper Archives",
      description: "Historical and current newspaper collections.",
      icon: FaArchive,
      category: "Archive",
      access: "On-campus Only"
    }
  ];

  const studySpaces = [
    {
      id: 1,
      name: "Silent Study Area",
      description: "Quiet environment for focused individual study.",
      capacity: "50 seats",
      amenities: ["Individual desks", "Power outlets", "Natural lighting"],
      icon: FaBookOpen,
      availability: "6:00 AM - 10:00 PM"
    },
    {
      id: 2,
      name: "Group Study Rooms",
      description: "Collaborative spaces for group projects and discussions.",
      capacity: "4-8 people per room",
      amenities: ["Whiteboards", "Projectors", "Video conferencing"],
      icon: FaUsers,
      availability: "Reservation Required"
    },
    {
      id: 3,
      name: "Computer Lab",
      description: "Modern computer workstations with specialized software.",
      capacity: "30 workstations",
      amenities: ["High-speed internet", "Specialized software", "Printing facilities"],
      icon: FaDesktop,
      availability: "8:00 AM - 8:00 PM"
    },
    {
      id: 4,
      name: "Reading Lounge",
      description: "Comfortable seating area for casual reading and relaxation.",
      capacity: "25 seats",
      amenities: ["Comfortable chairs", "Coffee station", "Magazine collection"],
      icon: FaCoffee,
      availability: "6:00 AM - 10:00 PM"
    }
  ];

  const specialCollections = [
    {
      id: 1,
      name: "Rare Books Collection",
      description: "Historical and rare books dating back to the 18th century.",
      items: "500+ volumes",
      access: "By appointment only"
    },
    {
      id: 2,
      name: "University Archives",
      description: "Historical documents and records of the institution.",
      items: "10,000+ documents",
      access: "Research purposes"
    },
    {
      id: 3,
      name: "Digital Repository",
      description: "Institutional repository of research outputs and publications.",
      items: "2,000+ items",
      access: "Open access"
    },
    {
      id: 4,
      name: "Thesis Collection",
      description: "Complete collection of student theses and dissertations.",
      items: "1,500+ theses",
      access: "On-campus only"
    }
  ];

  const libraryEvents = [
    {
      id: 1,
      title: "Research Skills Workshop",
      date: "March 15, 2024",
      time: "2:00 PM - 4:00 PM",
      location: "Library Conference Room",
      description: "Learn advanced research techniques and database navigation.",
      type: "Workshop"
    },
    {
      id: 2,
      title: "Citation Management Training",
      date: "March 22, 2024",
      time: "10:00 AM - 12:00 PM",
      location: "Computer Lab",
      description: "Master Zotero and Mendeley for efficient citation management.",
      type: "Training"
    },
    {
      id: 3,
      title: "Digital Literacy Seminar",
      date: "March 29, 2024",
      time: "3:00 PM - 5:00 PM",
      location: "Library Auditorium",
      description: "Understanding digital resources and online research ethics.",
      type: "Seminar"
    },
    {
      id: 4,
      title: "Book Club Meeting",
      date: "April 5, 2024",
      time: "6:00 PM - 8:00 PM",
      location: "Reading Lounge",
      description: "Monthly book discussion on 'The Future of Technology'.",
      type: "Social"
    }
  ];

  const categories = ["all", "Database", "E-books", "Multimedia", "Archive"];

  const filteredResources = digitalResources.filter(resource => {
    const matchesSearch = resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || resource.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });


  return (
    <div>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-custom-pink via-purple-900 to-indigo-900 text-white py-20 lg:py-32">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Library & Learning Resources
          </h1>
          <p className="text-xl sm:text-2xl mb-8 max-w-3xl mx-auto">
            Your gateway to knowledge, research, and academic excellence. Access our comprehensive collection of resources and services.
          </p>
          <div className="flex flex-wrap justify-center gap-8 text-center">
            <div className="flex items-center gap-2">
              <FaBook className="text-gold text-2xl" />
              <span className="text-lg font-semibold">50,000+ Books</span>
            </div>
            <div className="flex items-center gap-2">
              <FaDatabase className="text-gold text-2xl" />
              <span className="text-lg font-semibold">100+ Databases</span>
            </div>
            <div className="flex items-center gap-2">
              <FaUsers className="text-gold text-2xl" />
              <span className="text-lg font-semibold">24/7 Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Library Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">
              Library Services
            </h2>
            <p className="text-lg text-gray-600">
              Comprehensive services to support your academic and research needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {libraryServices.map((service) => {
              const IconComponent = service.icon;
              return (
                <div key={service.id} className="bg-gray-50 rounded-xl p-8 text-center hover:bg-white hover:shadow-lg transition-all duration-300">
                  <div className="bg-custom-pink text-white p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <IconComponent className="text-2xl" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm">{service.description}</p>
                  <div className="space-y-2 mb-4">
                    {service.features.map((feature, index) => (
                      <div key={index} className="text-sm text-gray-500">• {feature}</div>
                    ))}
                  </div>
                  <div className="text-xs text-gold font-semibold">
                    {service.availability}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Digital Resources */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">
              Digital Resources
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Access our extensive collection of digital resources and databases
            </p>
            
            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search resources..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-pink focus:border-transparent"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <FaFilter className="text-gray-600" />
                <span>Filter</span>
              </button>
            </div>

            {/* Filter Options */}
            {showFilters && (
              <div className="mb-8 p-4 bg-white rounded-lg">
                <div className="flex flex-wrap gap-4 justify-center">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-pink focus:border-transparent"
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>
                          {category === "all" ? "All Categories" : category}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredResources.map((resource) => {
              const IconComponent = resource.icon;
              return (
                <div key={resource.id} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="bg-custom-pink text-white p-3 rounded-lg">
                      <IconComponent className="text-xl" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{resource.name}</h3>
                      <p className="text-gray-600 text-sm mb-2">{resource.description}</p>
                      <span className="inline-block bg-gold text-white px-2 py-1 rounded text-xs font-semibold">
                        {resource.category}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <FaGlobe className="text-custom-pink" />
                      {resource.access}
                    </span>
                    <button className="text-custom-pink hover:text-gold transition-colors">
                      <FaArrowRight />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Study Spaces */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">
              Study Spaces & Facilities
            </h2>
            <p className="text-lg text-gray-600">
              Comfortable and well-equipped spaces for all your study needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {studySpaces.map((space) => {
              const IconComponent = space.icon;
              return (
                <div key={space.id} className="bg-gray-50 rounded-xl p-6 hover:bg-white hover:shadow-lg transition-all duration-300">
                  <div className="text-center mb-4">
                    <div className="bg-custom-pink text-white p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <IconComponent className="text-2xl" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{space.name}</h3>
                    <p className="text-gray-600 text-sm mb-3">{space.description}</p>
                    <div className="text-sm text-gold font-semibold mb-4">{space.capacity}</div>
                  </div>
                  <div className="space-y-2 mb-4">
                    <h4 className="font-semibold text-gray-900 text-sm">Amenities:</h4>
                    {space.amenities.map((amenity, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-1.5 h-1.5 bg-custom-pink rounded-full"></div>
                        {amenity}
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-gray-500 text-center">
                    {space.availability}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Library Hours & Contact */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-6">
                Library Hours & Contact
              </h2>
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FaClock className="text-custom-pink" />
                    Operating Hours
                  </h3>
                  <div className="space-y-2 text-gray-600">
                    <div className="flex justify-between">
                      <span>Monday - Friday:</span>
                      <span className="font-semibold">6:00 AM - 10:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Saturday:</span>
                      <span className="font-semibold">8:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sunday:</span>
                      <span className="font-semibold">10:00 AM - 4:00 PM</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FaPhone className="text-custom-pink" />
                    Contact Information
                  </h3>
                  <div className="space-y-3 text-gray-600">
                    <div className="flex items-center gap-3">
                      <FaPhone className="text-custom-pink" />
                      <span>+255 22 123 4567</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <FaEnvelope className="text-custom-pink" />
                      <span>library@jriit.ac.tz</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <FaMapMarkerAlt className="text-custom-pink" />
                      <span>Building F, Ground Floor</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-6">
                Special Collections
              </h2>
              <div className="space-y-4">
                {specialCollections.map((collection) => (
                  <div key={collection.id} className="bg-white rounded-xl p-6 shadow-lg">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{collection.name}</h3>
                    <p className="text-gray-600 text-sm mb-2">{collection.description}</p>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>{collection.items}</span>
                      <span className="font-semibold">{collection.access}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Library Events */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">
              Library Events & Workshops
            </h2>
            <p className="text-lg text-gray-600">
              Join our educational events and workshops to enhance your research skills
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {libraryEvents.map((event) => (
              <div key={event.id} className="bg-gray-50 rounded-xl p-6 hover:bg-white hover:shadow-lg transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="bg-custom-pink text-white p-3 rounded-lg">
                    <FaCalendarAlt className="text-xl" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{event.title}</h3>
                    <p className="text-gray-600 text-sm mb-3">{event.description}</p>
                    <div className="space-y-1 text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <FaClock className="text-custom-pink" />
                        <span>{event.date} at {event.time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FaMapMarkerAlt className="text-custom-pink" />
                        <span>{event.location}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FaTag className="text-custom-pink" />
                        <span className="bg-gold text-white px-2 py-1 rounded text-xs font-semibold">
                          {event.type}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-custom-pink to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Need Help with Your Research?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Our expert librarians are here to help you find the resources you need for your academic success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-gold text-white px-8 py-3 rounded-lg hover:bg-yellow-500 transition-colors duration-300 font-semibold">
              Get Research Help
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg hover:bg-white hover:text-custom-pink transition-colors duration-300 font-semibold">
              Visit Library
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default Library;

